import { action, computed, makeObservable } from 'mobx';

import { PricelistProductFamilyData } from '../../../app/data/model';
import SelectionState from './SelectionState';

/**
 * Family-aware view over the flat product {@link SelectionState}: resolves a family id to its
 * product ids and derives aggregate state, so a family row can select all of its products and
 * reflect a checked / indeterminate / empty checkbox. Only products are actually selectable.
 */
export default class ProductSelectionState {
  private readonly selection = new SelectionState();

  public constructor(private readonly getFamilies: () => PricelistProductFamilyData[]) {
    makeObservable(this);
  }

  public get selectedIds(): ReadonlySet<string> {
    return this.selection.selectedIds;
  }

  public get selectedCount(): number {
    return this.selection.selectedCount;
  }

  public get hasSelection(): boolean {
    return this.selection.hasSelection;
  }

  @computed
  private get productIdsByFamily(): Map<string, string[]> {
    return new Map(this.getFamilies().map((family) => [family.id, family.products.map((product) => product.id)]));
  }

  @computed
  private get allProductIds(): string[] {
    return this.getFamilies().flatMap((family) => family.products.map((product) => product.id));
  }

  /** A family id resolves to all of its product ids; any other id is a product id on its own. */
  private toProductIds(id: string): string[] {
    return this.productIdsByFamily.get(id) ?? [id];
  }

  @computed
  public get isAllSelected(): boolean {
    return this.selection.areAllSelected(this.allProductIds);
  }

  @computed
  public get isAllIndeterminate(): boolean {
    return this.selection.hasSelection && !this.isAllSelected;
  }

  /** True when every product the id covers is selected (a lone product, or a whole family). */
  public isSelected(id: string): boolean {
    return this.selection.areAllSelected(this.toProductIds(id));
  }

  /** True for a family with some — but not all — of its products selected. */
  public isIndeterminate(id: string): boolean {
    const familyProductIds = this.productIdsByFamily.get(id);

    return familyProductIds
      ? this.selection.areSomeSelected(familyProductIds) && !this.selection.areAllSelected(familyProductIds)
      : false;
  }

  @action.bound
  public toggle(id: string, isSelected: boolean): void {
    const ids = this.toProductIds(id);

    if (isSelected) {
      this.selection.select(ids);
    } else {
      this.selection.deselect(ids);
    }
  }

  @action.bound
  public toggleAll(isSelected: boolean): void {
    if (isSelected) {
      this.selection.select(this.allProductIds);
    } else {
      this.selection.clear();
    }
  }

  @action.bound
  public clear(): void {
    this.selection.clear();
  }
}
